import { noop } from 'lodash';
import { useState } from 'react';
import { ModalTemplate } from '../components/ModalTemplate';
import { modal } from '../utils/modal';
import { handleOnConfirmAsync } from '../utils/handleOnConfirmAsync';
import { ConfirmConfig, ConfirmDialogProps, ConfirmResult } from './types';
import { ModalSize } from '../types';

export const ConfirmDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = 'Ok',
  confirmButtonClickId,
  confirmButtonDataTestId,
  cancelButtonTitle = 'Cancel',
  cancelButtonClickId,
  cancelButtonDataTestId,
  isConfirmButtonDanger,
  doqType,
  onConfirm,
  onCancel,
  ...restOfDialogBaseProps
}: ConfirmDialogProps) => {
  const [rememberAction, setRememberAction] = useState<boolean>(false);
  const onConfirmProp = () =>
    onConfirm({
      ...(showRememberActionCheckbox ? { rememberAction } : {}),
      confirmed: true,
    });
  return (
    <ModalTemplate
      headerText={title}
      errorBlockMessage={errorMessage}
      rememberActionCheckboxState={rememberAction}
      onRememberActionCheckboxChange={setRememberAction}
      rememberActionCustomCheckboxText={
        rememberActionCustomCheckboxText || 'Don’t ask me again'
      }
      showRememberActionCheckbox={showRememberActionCheckbox}
      primaryButtonText={!isConfirmButtonDanger ? confirmButtonTitle : ''}
      primaryButtonClickId={confirmButtonClickId}
      primaryButtonDataTestId={confirmButtonDataTestId}
      onPrimaryButtonClick={onConfirmProp}
      dangerButtonText={isConfirmButtonDanger ? confirmButtonTitle : ''}
      dangerButtonClickId={confirmButtonClickId}
      dangerButtonDataTestId={confirmButtonDataTestId}
      onDangerButtonClick={onConfirmProp}
      secondaryButtonText={cancelButtonTitle}
      secondaryButtonClickId={cancelButtonClickId}
      secondaryButtonDataTestId={cancelButtonDataTestId}
      onSecondaryButtonClick={onCancel}
      doqType={doqType}
      {...restOfDialogBaseProps}
    >
      {body}
    </ModalTemplate>
  );
};

export const ConfirmDeleteDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = 'Delete',
  confirmButtonClickId,
  confirmButtonDataTestId,
  cancelButtonTitle = 'Cancel',
  modalSize = ModalSize.S,
  cancelButtonClickId,
  cancelButtonDataTestId,
  isConfirmButtonDanger = true,
  doqType,
  onConfirm,
  onCancel,
  ...restOfDialogBaseProps
}: ConfirmDialogProps) => {
  const [rememberAction, setRememberAction] = useState<boolean>(false);
  const onConfirmProp = () =>
    onConfirm({
      ...(showRememberActionCheckbox ? { rememberAction } : {}),
      confirmed: true,
    });
  return (
    <ModalTemplate
      headerText={title}
      errorBlockMessage={errorMessage}
      rememberActionCheckboxState={rememberAction}
      onRememberActionCheckboxChange={setRememberAction}
      rememberActionCustomCheckboxText={
        rememberActionCustomCheckboxText || 'Don’t ask me again'
      }
      showRememberActionCheckbox={showRememberActionCheckbox}
      primaryButtonClickId={confirmButtonClickId}
      primaryButtonDataTestId={confirmButtonDataTestId}
      onPrimaryButtonClick={onConfirmProp}
      dangerButtonText={isConfirmButtonDanger ? confirmButtonTitle : ''}
      dangerButtonClickId={confirmButtonClickId}
      dangerButtonDataTestId={confirmButtonDataTestId}
      onDangerButtonClick={onConfirmProp}
      secondaryButtonText={cancelButtonTitle}
      secondaryButtonClickId={cancelButtonClickId}
      secondaryButtonDataTestId={cancelButtonDataTestId}
      onSecondaryButtonClick={onCancel}
      doqType={doqType}
      modalSize={modalSize}
      {...restOfDialogBaseProps}
    >
      {body}
    </ModalTemplate>
  );
};

/**
 * Use this function when you would like to launch a Confirm dialog from
 * anywhere in the app. The function is async so it should be treated as that.
 * Returns a promise
 * @async
 * @param config
 * @returns
 * @deprecated Avoid this in new code. Use `AnimatedModal` and control `isOpen`
 * like any other state. See #19797
 */
export const confirm = ({
  onConfirmAsync = noop,
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  errorAlertTitle,
  errorAlertSubtitle,
  ...config
}: ConfirmConfig) => {
  return modal<ConfirmResult>(
    resolve => (
      <ConfirmDialog
        {...config}
        onConfirm={handleOnConfirmAsync({
          onConfirmAsync,
          resolve,
          errorAlertTitle,
          errorAlertSubtitle,
        })}
        onCancel={() => resolve(false)}
      />
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};

/**
 * @deprecated Avoid this in new code. Use `AnimatedModal` and control `isOpen`
 * like any other state. See #19797
 */
export const confirmDelete = ({
  isConfirmButtonDanger = true,
  confirmButtonTitle = 'Delete',
  onConfirmAsync = noop,
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  errorAlertTitle,
  errorAlertSubtitle,
  ...config
}: ConfirmConfig) => {
  return modal<ConfirmResult>(
    resolve => (
      <ConfirmDialog
        isConfirmButtonDanger={isConfirmButtonDanger}
        confirmButtonTitle={confirmButtonTitle}
        onConfirm={handleOnConfirmAsync({
          onConfirmAsync,
          resolve,
          errorAlertTitle,
          errorAlertSubtitle,
        })}
        onCancel={() => resolve(false)}
        {...config}
      />
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
